import React from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../utils/cn';

export interface SelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

export interface SelectProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  label?: string;
  error?: string;
  helperText?: string;
  size?: 'xs' | 'sm' | 'md';
  options?: SelectOption[];
}

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      label,
      error,
      helperText,
      size = 'md',
      options,
      children,
      className = '',
      id,
      disabled,
      ...props
    },
    ref
  ) => {
    const generatedId = React.useId();
    const inputId = id || generatedId;

    const sizeStyles = {
      xs: 'text-xs pl-2.5 pr-7 py-1 rounded',
      sm: 'text-xs pl-3 pr-8 py-1.5 rounded-md',
      md: 'text-xs pl-3 pr-8 py-2 rounded-md',
    };

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className="block text-xs font-medium text-cf-textSecondary mb-1"
          >
            {label}
          </label>
        )}
        <div className="relative flex items-center">
          <select
            id={inputId}
            ref={ref}
            disabled={disabled}
            className={cn(
              'w-full bg-cf-input border border-cf-border text-cf-textPrimary focus:outline-none focus:border-cf-orange transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer appearance-none',
              sizeStyles[size],
              error && 'border-rose-500 focus:border-rose-500',
              className
            )}
            {...props}
          >
            {options
              ? options.map((opt) => (
                  <option
                    key={opt.value}
                    value={opt.value}
                    disabled={opt.disabled}
                    className="bg-cf-surface text-cf-textPrimary"
                  >
                    {opt.label}
                  </option>
                ))
              : children}
          </select>
          <div className="absolute right-2.5 pointer-events-none text-cf-textMuted flex items-center">
            <ChevronDown size={size === 'xs' ? 12 : 14} />
          </div>
        </div>
        {error ? (
          <span className="block text-[11px] text-rose-600 dark:text-rose-400 mt-1">
            {error}
          </span>
        ) : helperText ? (
          <span className="block text-[11px] text-cf-textMuted mt-1">
            {helperText}
          </span>
        ) : null}
      </div>
    );
  }
);

Select.displayName = 'Select';
